// src/pages/TestcaseManagement.jsx

import React, { useState, useEffect } from "react";
import axios from "../axios"; // your configured Axios instance

export default function TestcaseManagement() {
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [suites, setSuites] = useState([]);
    const [selectedSuite, setSelectedSuite] = useState("");
    const [testcases, setTestcases] = useState([]);
    const [bulkFile, setBulkFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    // Inline “Add Suite” form state
    const [addingSuite, setAddingSuite] = useState(false);
    const [newSuiteName, setNewSuiteName] = useState("");

    // 1) Fetch all projects on mount
    useEffect(() => {
        const fetchProjects = async () => {
            setIsLoading(true);
            setErrorMsg("");
            try {
                const res = await axios.get("/projects");
                const data = res.data;
                if (data.success && Array.isArray(data.projects)) {
                    setProjects(data.projects);
                } else if (Array.isArray(data)) {
                    setProjects(data);
                } else {
                    setProjects([]);
                }
            } catch (err) {
                console.error("Failed to load projects:", err);
                setErrorMsg("Failed to load projects");
                setProjects([]);
            } finally {
                setIsLoading(false);
            }
        };
        fetchProjects();
    }, []);

    // 2) Load suites when a project is selected
    const handleProjectSelect = async (projectId) => {
        setSelectedProject(projectId);
        setSelectedSuite("");
        setSuites([]);
        setTestcases([]);
        setAddingSuite(false);
        setNewSuiteName("");
        setErrorMsg("");

        if (projectId == null) return;

        setIsLoading(true);
        try {
            const res = await axios.get(`/suites/${projectId}`);
            const data = res.data;
            let fetchedSuites = [];
            if (data.success && Array.isArray(data.suites)) {
                fetchedSuites = data.suites;
            } else if (Array.isArray(data)) {
                fetchedSuites = data;
            }
            setSuites(fetchedSuites);
        } catch (err) {
            console.error("Failed to load suites:", err);
            setErrorMsg("Failed to load suites");
            setSuites([]);
        } finally {
            setIsLoading(false);
        }
    };

    // 3) Load testcases when a suite is selected
    const handleSuiteSelect = async (suiteName) => {
        setSelectedSuite(suiteName);
        setTestcases([]);
        setErrorMsg("");

        if (!suiteName) return;

        setIsLoading(true);
        try {
            const res = await axios.get(
                `/testcases/${encodeURIComponent(suiteName)}`
            );
            const data = res.data;
            let fetched = [];
            if (data.success && Array.isArray(data.testcases)) {
                fetched = data.testcases;
            } else if (Array.isArray(data)) {
                fetched = data;
            }
            setTestcases(fetched);
        } catch (err) {
            console.error("Failed to load testcases:", err);
            setErrorMsg("Failed to load testcases");
            setTestcases([]);
        } finally {
            setIsLoading(false);
        }
    };

    // 4) Bulk import handler
    const handleFileChange = (e) => setBulkFile(e.target.files[0]);
    const handleBulkImport = async () => {
        if (!bulkFile || !selectedSuite) {
            return alert("Select a suite and a file to import.");
        }
        const formData = new FormData();
        formData.append("file", bulkFile);
        formData.append("suite_name", selectedSuite);

        setIsLoading(true);
        setErrorMsg("");
        try {
            await axios.post("/testcases/bulk", formData);
            setBulkFile(null);
            document.getElementById("fileInput").value = "";
            alert("Imported successfully!");
            handleSuiteSelect(selectedSuite);
        } catch (err) {
            console.error("Bulk import failed:", err);
            setErrorMsg(
                err.response?.data?.error
                    ? `Bulk import failed: ${err.response.data.error}`
                    : "Bulk import failed"
            );
        } finally {
            setIsLoading(false);
        }
    };

    // 5) Save new suite (inline)
    const handleSaveNewSuite = async () => {
        if (!newSuiteName.trim()) {
            return alert("Suite name cannot be empty.");
        }
        const projectObj = projects.find(
            (p) =>
                p.id === selectedProject || p.project_id === selectedProject
        );
        const projectName = projectObj?.project_name || "";

        setIsLoading(true);
        setErrorMsg("");
        try {
            await axios.post("/suites", {
                project_id: selectedProject,
                name: newSuiteName.trim(),
                project_name: projectName,
            });
            // refresh suites
            const res = await axios.get(`/suites/${selectedProject}`);
            const data = res.data;
            if (data.success && Array.isArray(data.suites)) {
                setSuites(data.suites);
            } else if (Array.isArray(data)) {
                setSuites(data);
            }
            setAddingSuite(false);
            setNewSuiteName("");
            alert("Suite added successfully!");
        } catch (err) {
            console.error("Failed to add suite:", err);
            setErrorMsg(
                err.response?.data?.error
                    ? `Could not add suite: ${err.response.data.error}`
                    : "Could not add suite"
            );
        } finally {
            setIsLoading(false);
        }
    };

    // 6) Cancel adding suite
    const handleCancelNewSuite = () => {
        setAddingSuite(false);
        setNewSuiteName("");
        setErrorMsg("");
    };

    return (
        <div className="p-6 bg-white">
            <h1 className="text-2xl font-bold mb-4">🧪 Testcase Management</h1>

            {/* STEP 1: Project Selection */}
            {selectedProject == null ? (
                <section className="mb-8 bg-white p-6 rounded shadow">
                    <h2 className="text-lg font-semibold text-gray-700 mb-3">
                        Select a Project
                    </h2>
                    {isLoading ? (
                        <div className="text-center text-gray-500 py-4">
                            Loading projects…
                        </div>
                    ) : errorMsg ? (
                        <div className="text-center text-red-500 py-4">
                            {errorMsg}
                        </div>
                    ) : projects.length === 0 ? (
                        <div className="text-center text-gray-500 py-4">
                            No projects available
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                            {projects.map((proj) => {
                                const projectKey =
                                    proj.id ?? proj.project_id ?? proj._id;
                                return (
                                    <button
                                        key={projectKey}
                                        onClick={() => handleProjectSelect(projectKey)}
                                        className={`border rounded-lg p-2 text-left transition-shadow w-full ${
                                            String(selectedProject) === String(projectKey)
                                                ? "bg-indigo-100 border-indigo-400 shadow-md"
                                                : "bg-white border-gray-200 hover:shadow-lg"
                                        }`}
                                    >
                                        <p className="text-lg font-semibold text-indigo-700 truncate">
                                            {proj.project_name}
                                        </p>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </section>
            ) : (
                <section className="mb-6 bg-white p-4 rounded shadow flex items-center justify-between">
                    <p className="text-lg text-gray-800">
                        Project:&nbsp;
                        <span className="font-semibold text-indigo-700">
              {
                  projects.find((p) => {
                      const key = p.id ?? p.project_id ?? p._id;
                      return String(key) === String(selectedProject);
                  })?.project_name
              }
            </span>
                    </p>
                    <button
                        onClick={() => {
                            setSelectedProject(null);
                            setSelectedSuite("");
                            setSuites([]);
                            setTestcases([]);
                            setAddingSuite(false);
                            setNewSuiteName("");
                            setErrorMsg("");
                        }}
                        className="text-sm px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded"
                    >
                        Change Project
                    </button>
                </section>
            )}

            {/* STEP 2: Suite Selector & Bulk Import */}
            {selectedProject != null && (
                <section className="mb-8 bg-white p-6 rounded shadow">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        {/* Suite Selector */}
                        <div>
                            <label className="block font-semibold text-gray-700">
                                Select Suite
                            </label>
                            <select
                                value={selectedSuite}
                                onChange={(e) => handleSuiteSelect(e.target.value)}
                                disabled={!selectedProject}
                                className="w-full border rounded px-3 py-2 mt-1 disabled:opacity-50"
                            >
                                <option value="">-- Choose suite --</option>
                                {suites.map((s) => (
                                    <option key={s.suite_id} value={s.name}>
                                        {s.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Bulk Import & Add Suite */}
                        <div className="md:col-span-3">
                            <label className="block font-semibold text-gray-700">
                                Bulk Import Testcases &amp; Add Suite
                            </label>
                            <div className="flex mt-1 gap-2 items-center">
                                <input
                                    id="fileInput"
                                    type="file"
                                    accept=".xlsx,.xls,.csv"
                                    onChange={handleFileChange}
                                    className="border rounded px-3 py-2 flex-1"
                                />
                                <button
                                    onClick={handleBulkImport}
                                    disabled={!bulkFile || !selectedSuite}
                                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-gray-300"
                                >
                                    Import
                                </button>
                                {!addingSuite ? (
                                    <button
                                        onClick={() => {
                                            setAddingSuite(true);
                                            setErrorMsg("");
                                        }}
                                        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                                    >
                                        Add Suite
                                    </button>
                                ) : (
                                    <div className="flex gap-2 items-center">
                                        <input
                                            type="text"
                                            value={newSuiteName}
                                            onChange={(e) => setNewSuiteName(e.target.value)}
                                            placeholder="New suite name"
                                            className="border rounded px-3 py-2"
                                        />
                                        <button
                                            onClick={handleSaveNewSuite}
                                            className="bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700"
                                        >
                                            Save
                                        </button>
                                        <button
                                            onClick={handleCancelNewSuite}
                                            className="bg-gray-300 text-gray-700 px-3 py-2 rounded hover:bg-gray-400"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                )}
                            </div>
                            {errorMsg && (
                                <p className="mt-2 text-red-500 text-sm">{errorMsg}</p>
                            )}
                        </div>
                    </div>
                </section>
            )}

            {/* STEP 3: Testcase Table */}
            {selectedProject != null && (
                <section className="bg-white rounded shadow p-6">
                    {isLoading ? (
                        <p className="text-center text-gray-500">Loading…</p>
                    ) : errorMsg ? (
                        <p className="text-center text-red-500">{errorMsg}</p>
                    ) : selectedSuite === "" ? (
                        <p className="text-center text-gray-500">
                            Select a suite to see testcases.
                        </p>
                    ) : testcases.length === 0 ? (
                        <p className="text-center text-gray-500">
                            No testcases found in this suite.
                        </p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full border table-auto text-sm">
                                <thead className="bg-blue-50 text-gray-600 uppercase text-xs">
                                <tr>
                                    <th className="border px-4 py-2 text-left">TITLE</th>
                                    <th className="border px-4 py-2 text-left">DESCRIPTION</th>
                                    <th className="border px-4 py-2 text-left">STEPS</th>
                                    <th className="border px-4 py-2 text-left">EXPECTED</th>
                                    <th className="border px-4 py-2 text-left">CREATED BY</th>
                                </tr>
                                </thead>
                                <tbody>
                                {testcases.map((tc) => (
                                    <tr key={tc.id} className="hover:bg-gray-50">
                                        <td className="border px-4 py-2">{tc.title}</td>
                                        <td className="border px-4 py-2">{tc.description}</td>
                                        <td className="border px-4 py-2 whitespace-pre-wrap">
                                            {tc.steps}
                                        </td>
                                        <td className="border px-4 py-2">
                                            {tc.expected_result}
                                        </td>
                                        <td className="border px-4 py-2">{tc.created_by}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            )}
        </div>
    );
}
